import { useTranslate } from 'ra-core';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/admin/spinner';
import { useCallback, useMemo, useState } from 'react';
import DataGrid, { type Column } from 'react-data-grid';
import { BulkOperationDialogGridEditCell } from './BulkOperationDialogGridEditCell';
import { BulkOperationDialogGridCell } from './BulkOperationDialogGridCell';
import { type CsvRecord, type GridRow, type GridRowValidator } from './types';
import { validateCsvSync } from './bulkValidation';
import { BulkOperationDialogGridFooter } from './BulkOperationDialogGridFooter';
import { useBulkTask } from './useBulkTask';
import { type SpreadsheetColumns } from '../../utils/spreadsheet/save';
import { type ZodCsvSchema, getZodCsvRowObjectSchema } from './bulkZodUtils';
import { ExportSheetButton } from './ExportSheetButton';
import { BulkOperationDialogFooter } from './BulkOperationDialogFooter';
import { TooltipProvider } from '../../components/ui/tooltip';

interface BulkOperationDialogGridProps<T extends CsvRecord = CsvRecord> {
  onNext: (records: T[]) => void;
  onBack: () => void;
  label: string;
  validateRow?: GridRowValidator<T>;
  schema: ZodCsvSchema<T>;
  data: T[];
  validationRateLimitDelay: number;
}

export function BulkOperationDialogGrid<T extends CsvRecord>({
  data,
  label,
  schema,
  validateRow,
  validationRateLimitDelay,
  onBack,
  onNext,
}: BulkOperationDialogGridProps<T>) {
  const translate = useTranslate();
  const [gridData, setGridData] = useState(() =>
    validateCsvSync([], data, schema)
  );

  const handleRowsChange = useCallback(
    (rows: GridRow<T>[]) =>
      setGridData((oldData) =>
        validateCsvSync(
          oldData,
          rows.map((r) => r.data),
          schema
        )
      ),
    [schema]
  );

  useBulkTask<T, GridRow<T>>({
    setData: setGridData,
    task: validateRow,
    taskRateLimitDelay: validationRateLimitDelay,
  });

  const columns = useMemo<Column<GridRow<T>>[]>(() => {
    const allKeys = Object.keys(
      getZodCsvRowObjectSchema(schema).shape
    ) as (keyof T & string)[];

    const cols = [
      {
        key: 'rowNumber',
        name: '',
        width: 40,
        renderCell: (props) => (
          <div className="flex items-center gap-2">
            {props.rowIdx + 1 /* rowIdx is 0-based, so add 1 for display */}
            {props.row.status === 'idle' || props.row.status === 'pending' ? (
              <Spinner size="small" className="size-4" />
            ) : null}
          </div>
        ),
      },
      ...allKeys.map<Column<GridRow<T>>>((k) => ({
        key: k,
        name: k,
        width: 'max-content',
        renderEditCell: (props) => (
          <BulkOperationDialogGridEditCell {...props} />
        ),
        renderCell: (props) => <BulkOperationDialogGridCell {...props} />,
      })),
    ] satisfies Column<GridRow<T>>[];

    return cols;
  }, [schema]);

  const canUpload = useMemo(
    () => gridData.every((row) => row.status === 'success'),
    [gridData]
  );

  return (
    <>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-hidden p-6">
        <TooltipProvider>
          <div className="min-h-0 min-w-0 flex-1 overflow-x-auto overflow-y-auto">
            <DataGrid
              className="rdg min-h-[22rem] w-max min-w-full"
              columns={columns}
              rows={gridData}
              rowKeyGetter={(row) => row.id}
              onRowsChange={handleRowsChange}
              defaultColumnOptions={{ resizable: true }}
              rowClass={(row) => {
                switch (row.status) {
                  case 'error':
                    return 'rdg-row-error';
                  default:
                    return undefined;
                }
              }}
            />
          </div>
        </TooltipProvider>

        <BulkOperationDialogGridFooter gridData={gridData} />
      </div>
      <BulkOperationDialogFooter className="shrink-0 flex-wrap">
        <Button type="button" variant="secondary" onClick={onBack}>
          {translate('moonstar.bulk_operation.actions.back', { _: 'Back' })}
        </Button>
        <ExportSheetButton
          label="moonstar.bulk_operation.actions.exportEdits"
          getSheet={() => {
            const data = gridData.map((row) => row.data);
            const columns = Object.keys(
              getZodCsvRowObjectSchema(schema).shape
            ) as SpreadsheetColumns<T>;

            return {
              basename: `${translate(label)} - Edits`,
              columns,
              data,
            };
          }}
        />
        <Button
          type="button"
          disabled={!canUpload}
          onClick={() => onNext(gridData.map((r) => r.data))}
        >
          {translate('moonstar.bulk_operation.actions.startProcessing', {
            _: 'Start processing',
          })}
        </Button>
      </BulkOperationDialogFooter>
    </>
  );
}
